import React, { Suspense } from "react";
import "@/i18n";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";

// Public layout & pages
import PublicLayout from "@/components/public/PublicLayout";
import Home from "@/pages/public/Home";
import NotFound from "@/pages/public/NotFound";
import PlaceholderPage from "@/components/common/PlaceholderPage";

// Admin
import AdminLayout from "@/components/admin/AdminLayout";
import AdminLogin from "@/pages/admin/Login";
import AdminDashboard from "@/pages/admin/Dashboard";
import SiteSettings from "@/pages/admin/SiteSettings";
import MediaLibrary from "@/pages/admin/MediaLibrary";
import Security from "@/pages/admin/Security";
import AdminHomepage from "@/pages/admin/Homepage";
import AdminSectorData from "@/pages/admin/SectorData";
import AdminCityData from "@/pages/admin/CityData";
import AdminNewsletter from "@/pages/admin/Newsletter";
import AdminContact from "@/pages/admin/AdminContact";
import AdminTools from "@/pages/admin/Tools";
import AdminPages from "@/pages/admin/Pages";
import AdminAIDrafts from "@/pages/admin/AIDrafts";
import AdminNewsletterCampaigns from "@/pages/admin/NewsletterCampaigns";
import AdminNewsSources from "@/pages/admin/NewsSources";
import AdminRawNews from "@/pages/admin/RawNews";
import AdminNewsAutomationSettings from "@/pages/admin/NewsAutomationSettings";
import AdminBlogPosts from "@/pages/admin/AdminBlogPosts";
import AdminBlogCategories from "@/pages/admin/AdminBlogCategories";
import AdminStories from "@/pages/admin/AdminStories";
import AdminStarRequirements from "@/pages/admin/AdminStarRequirements";
import AdminPlaceholder from "@/pages/admin/AdminPlaceholder";

// Public additional
import DataHub from "@/pages/public/DataHub";
import Contact from "@/pages/public/Contact";
import Tools from "@/pages/public/Tools";
import CostCalculator from "@/pages/public/CostCalculator";
import RoiCalculator from "@/pages/public/RoiCalculator";
import StaffCalculator from "@/pages/public/StaffCalculator";
import EnergyCalculator from "@/pages/public/EnergyCalculator";
import OccupancyCalculator from "@/pages/public/OccupancyCalculator";
import ShareResult from "@/pages/public/ShareResult";
import About from "@/pages/public/About";
import Brands from "@/pages/public/Brands";
import StarsList from "@/pages/public/StarsList";
import StarsDetail from "@/pages/public/StarsDetail";
import StarsCompare from "@/pages/public/StarsCompare";
import StarsChecklist from "@/pages/public/StarsChecklist";
import BlogList from "@/pages/public/BlogList";
import BlogDetail from "@/pages/public/BlogDetail";
import StoriesList from "@/pages/public/StoriesList";
import StoriesDetail from "@/pages/public/StoriesDetail";

// Initialize auth on first mount
useAuthStore.getState().checkAuth?.();

function PublicPlaceholder({ titleKey, path }) {
    return <PlaceholderPage titleKey={titleKey} path={path} />;
}

function App() {
    return (
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Yükleniyor...</div>}>
            <BrowserRouter>
                <Routes>
                    {/* Admin login (no layout) */}
                    <Route path="/admin/login" element={<AdminLogin />} />

                    {/* Admin (auth-guarded layout) */}
                    <Route path="/admin" element={<AdminLayout />}>
                        <Route index element={<AdminDashboard />} />
                        <Route path="veri-merkezi" element={<AdminSectorData />} />
                        <Route path="il-verileri" element={<AdminCityData />} />
                        <Route path="araclar" element={<AdminTools />} />
                        <Route path="yildiz-standartlari" element={<AdminStarRequirements />} />
                        <Route path="blog" element={<AdminBlogPosts />} />
                        <Route path="blog-kategorileri" element={<AdminBlogCategories />} />
                        <Route path="basari-hikayeleri" element={<AdminStories />} />
                        <Route path="newsletter" element={<AdminNewsletter />} />
                        <Route path="anasayfa" element={<AdminHomepage />} />
                        <Route path="sayfalar" element={<AdminPages />} />
                        <Route path="medya" element={<MediaLibrary />} />
                        <Route path="ayarlar" element={<SiteSettings />} />
                        <Route path="iletisim" element={<AdminContact />} />
                        <Route path="guvenlik" element={<Security />} />
                        <Route path="ai-taslaklari" element={<AdminAIDrafts />} />
                        <Route path="bultenler" element={<AdminNewsletterCampaigns />} />
                        <Route path="haber-kaynaklari" element={<AdminNewsSources />} />
                        <Route path="ham-haberler" element={<AdminRawNews />} />
                        <Route path="haber-ayarlari" element={<AdminNewsAutomationSettings />} />
                    </Route>

                    {/* Public layout */}
                    <Route element={<PublicLayout />}>
                        <Route index element={<Home />} />
                        <Route path="/veri-merkezi" element={<DataHub />} />
                        <Route path="/araclar" element={<Tools />} />
                        <Route path="/araclar/maliyet-hesaplayici" element={<CostCalculator />} />
                        <Route path="/araclar/roi-hesaplayici" element={<RoiCalculator />} />
                        <Route path="/araclar/personel-planlayici" element={<StaffCalculator />} />
                        <Route path="/araclar/enerji-hesaplayici" element={<EnergyCalculator />} />
                        <Route path="/araclar/doluluk-tahmincisi" element={<OccupancyCalculator />} />
                        <Route path="/yildiz-standartlari" element={<StarsList />} />
                        <Route path="/yildiz-standartlari/karsilastir" element={<StarsCompare />} />
                        <Route path="/yildiz-standartlari/kontrol" element={<StarsChecklist />} />
                        <Route path="/yildiz-standartlari/:star" element={<StarsDetail />} />
                        <Route path="/blog" element={<BlogList />} />
                        <Route path="/blog/:slug" element={<BlogDetail />} />
                        <Route path="/basari-hikayeleri" element={<StoriesList />} />
                        <Route path="/basari-hikayeleri/:slug" element={<StoriesDetail />} />
                        <Route path="/hakkimizda" element={<About />} />
                        <Route path="/markalarimiz" element={<Brands />} />
                        <Route path="/iletisim" element={<Contact />} />
                        <Route path="/sonuc/:id" element={<ShareResult />} />
                        <Route path="*" element={<NotFound />} />
                    </Route>
                </Routes>
            </BrowserRouter>
        </Suspense>
    );
}

export default App;
